import { useEffect, useState } from "react";
import axios from "axios";
import { SEARCH_DEBOUNCE_MS } from "../contactCardsManageConstants";

/**
 * Normalizes the search response. Accepts the canonical { contact_cards }
 * envelope as well as raw arrays / { data } fallbacks.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeSearch(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.contact_cards)) return data.contact_cards;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

/**
 * Performs the GET /search/<term> request and pushes results through setters.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchSearch({ term, signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/contact_cards/search/${encodeURIComponent(term)}`);
        if (!signal.aborted) setItems(normalizeSearch(data));
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible d'effectuer la recherche");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the debounced contact cards search state.
 * Clears results when the query is empty (caller falls back to the list).
 * @param {string} query - Current search query
 * @returns {{ items: Array, loading: boolean, error: string|null, active: boolean }}
 */
function useContactCardsSearch(query) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const trimmed = (query || "").trim();
    const active = trimmed.length > 0;

    useEffect(() => {
        if (!active) { setItems([]); setError(null); setLoading(false); return undefined; }
        const controller = new AbortController();
        const handle = setTimeout(() => {
            fetchSearch({ term: trimmed, signal: controller.signal, setItems, setError, setLoading });
        }, SEARCH_DEBOUNCE_MS);
        return () => { clearTimeout(handle); controller.abort(); };
    }, [trimmed, active]);

    return { items, loading, error, active };
}

export default useContactCardsSearch;
